import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Button, Container, Typography, Divider } from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import HaveQuestions from "../components/HaveQuestions";
import GradientIcon from "../components/GradientIcon";
import caseStudyBg from "../assets/CaseStudy/caseStudy-bg.png";
import halfCircles from "../assets/half-circles.png";
import caseStudyApp from "../assets/CaseStudy/case-study-app-screenshot.png";
import caseStudyApp1 from "../assets/CaseStudy/case-study-app-screenshot1.png";

// import caseStudyAppTab from "../assets/CaseStudy/case-study-app-screenshot-tab.png";
// import caseStudyApp1Tab from "../assets/CaseStudy/case-study-app-screenshot1-tab.png";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import FilterVintageOutlinedIcon from "@mui/icons-material/FilterVintageOutlined";
import caseStudyImg1 from "../assets/CaseStudy/image1.jpg";
import caseStudyImg2 from "../assets/CaseStudy/image2.jpg";
import caseStudyImg3 from "../assets/CaseStudy/image3.jpg";
import caseStudyImg4 from "../assets/CaseStudy/image4.jpg";

const gradientHoverSx = {
  position: "relative",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
  transition: "all 0.3s ease",
  "&:hover::before": {
    opacity: 1,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "16px",
    padding: "2px",
    background: "linear-gradient(90deg, #FF7FAC, #5834BA)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
};

const ApproachCard = ({ title, description, icon = ExtensionOutlinedIcon }) => (
  <Box
    sx={{
      width: "408px",
      height: "306px",

      "@media (max-width: 1400px)": {
        width: "100%",
        height: "326px",
      },
      "@media (max-width: 819px)": {
        width: "100%",
        height: "auto",
      },
      px: "16px",
      py: "24px",
      borderRadius: "16px",
      background: "linear-gradient(135deg, #FC86AF0D, #724FD30D)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      ...gradientHoverSx,
    }}
  >
    <GradientIcon
      icon={icon}
      size={40}
      bordered
      boxSize={64}
      borderRadius={16}
      bgColor="transparent"
    />
    <Typography
      sx={{
        fontSize: "20px",
        fontWeight: 700,
        color: "#2A2177",
        mt: 1,
        mb: 1,
        alignSelf: "flex-start",
        letterSpacing: "-0.4px",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.875rem",
        color: "#00000099",
        lineHeight: 1.6,
      }}
    >
      {description}
    </Typography>
  </Box>
);

const CaseStudy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ backgroundColor: "#FFFFFF" }}>
      <Helmet>
        <title>DAM Journey - Case Study</title>
        <meta
          name="description"
          content="Transforming Digital Assets for a Global Manufacturer, How we helped a global manufacturer streamline digital asset management, improve collaboration, and accelerate time-to-market"
        />
        <meta
          property="keywords"
          content="DAM, DAM Journey, DAM Practice, DAM Consultant, DAM Consulting, DAM Practitioner, DAM Service, DAM Product, DAM Discovery, DAM Implementation, DAM Deployment, Transforming Digital Assets for a Global Manufacturer"
        />
      </Helmet>
      <Box
        sx={
          {
            /*maxWidth: "1440px"*/
          }
        }
      >
        {/* Hero Banner Section */}
        <Box
          sx={{
            width: "100%",
            height: "350px",
            background: "linear-gradient(0deg, #2A2177 0%, #0624BE 100%)",
            display: "flex",
            alignItems: "center",
            px: "84px",
            py: "74px",
            overflow: "hidden",
            position: "relative",
            "@media (max-width: 1400px)": {
              px: "24px",
              py: "78px",
            },
            "@media (max-width: 819px)": {
              flexDirection: "column",
              textAlign: "center",
              px: "16px",
              pt: "48px",
              pb: 2,
              height: "420px",
            },
          }}
        >
          {/* Left Text Content */}
          <Box
            sx={{
              flex: 2,
              zIndex: 1,
              gap: 2,
              "@media (max-width: 1400px)": {
                maxWidth: "348px",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: "regular",
                color: "#ffffff",
                lineHeight: 2,
                maxWidth: "624px",
              }}
            >
              CASE STUDY
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: "2.5rem",
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 1.3,
                mb: 2,
                "@media (max-width: 1400px)": {
                  fontSize: "1.75rem",
                  fontWeight: "semiBold",
                },
                "@media (max-width: 819px)": {
                  fontSize: "1.5rem",
                  mb: 1,
                },
              }}
            >
              Transforming Digital Assets for a Global Manufacturer.
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "regular",
                color: "#ffffff",
                lineHeight: 1.7,
                maxWidth: "624px",
                "@media (max-width: 1400px)": {
                  fontSize: "18px",
                },
                "@media (max-width: 819px)": {
                  fontSize: "16px",
                },
              }}
            >
              How we helped a global manufacturer streamline digital asset
              management, improve collaboration, and accelerate time-to-market.
            </Typography>
          </Box>

          {/* Right Infographic Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              position: "relative",
              "@media (max-width: 1400px)": {
                justifyContent: "flex-end",
              },
            }}
          >
            {/* Background Half Circles */}
            <Box
              component="img"
              src={halfCircles}
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: "599px",
                height: "350px",
                objectFit: "contain",
                left: -100,
                "@media (max-width: 1400px)": {
                  display: "none",
                },
                zIndex: 0,
              }}
            />
            {/* Foreground caseStudy-bg */}
            <Box
              component="img"
              src={caseStudyBg}
              sx={{
                position: "relative",
                zIndex: 1,
                width: "515px",
                height: "290px",
                "@media (max-width: 1400px)": {
                  width: "355px",
                  height: "200px",
                },
                "@media (max-width: 819px)": {
                  fontSize: "18px",
                  width: "265px",
                  height: "149px",
                },
              }}
            />
          </Box>
        </Box>

        {/* The Challenge Section */}
        <Box
          sx={{
            minHeight: "462px",
            px: "84px",
            "@media (max-width: 1400px)": {
              px: "24px",
            },
            "@media (max-width: 819px)": {
              px: "16px",
            },
            pt: "48px",
            pb: "24px",
            boxSizing: "border-box",
          }}
        >
          {/* Section Header */}
          <Typography
            sx={{
              fontSize: "3rem",
              "@media (max-width: 1400px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 819px)": {
                fontSize: "2rem",
              },
              fontWeight: 700,
              color: "#2A2177",
              lineHeight: 1.2,
              mb: 4,
            }}
          >
            The Challenge
          </Typography>

          {/* Content Row  */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              alignItems: "flex-start",
              "@media (max-width: 1400px)": {
                flexDirection: "column",
              },
            }}
          >
            {/* Left Column */}
            <Box sx={{ flex: 1 }}>
              {/* Description Text */}
              <Typography
                sx={{
                  fontSize: "18px",
                  color: "#333333",
                  lineHeight: 1.7,
                  mb: "55px",
                }}
              >
                The client, a leading global manufacturer, was struggling with
                fragmented digital assets across departments, inconsistent
                metadata, and slow approval workflows.
              </Typography>

              {/* Gray Box - Challenges List */}
              <Box
                sx={{
                  backgroundColor: "#F9F9F9",
                  borderRadius: "16px",
                  py: "58px",
                  pr: "16px",
                  pl: "24px",
                  display: "flex",
                  alignItems: "center",
                  maxWidth: "840px",
                  "@media (max-width: 1400px)": {
                    maxWidth: "100%",
                  },
                  "@media (max-width: 819px)": {
                    flexDirection: "column",
                    pl: "16px",
                    py: "24px",
                  },
                  gap: 3,
                }}
              >
                {/* Document Icon */}
                <Box>
                  <DescriptionOutlinedIcon
                    sx={{ width: 40, height: 40, color: "#2A2177" }}
                  />
                </Box>

                {/* Challenge Items */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[
                    "Siloed systems and disconnected teams led to duplication and version conflicts.",
                    "Search and retrieval of assets were time-consuming and inefficient.",
                    "Limited visibility into asset usage and lifecycle reduced operational efficiency.",
                    "Manual content management processes slowed collaboration and delayed project delivery.",
                  ].map((text, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <GradientIcon
                        icon={CheckCircleOutlinedIcon}
                        size={20}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                      <Typography sx={{ fontSize: "1rem", color: "#333333" }}>
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Right Box - Quick Facts */}
            <Box
              sx={{
                width: "408px",
                height: "374px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #0000001A",
                borderRadius: "16px",
                boxShadow: "0px 4px 4px 0px #0000001A",
                p: 2,
                display: "flex",
                flexDirection: "column",
                "@media (max-width: 1400px)": {
                  width: "100%",
                },
              }}
            >
              {/* Quick Facts Header */}
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: "#2A2177",
                  mb: 0,
                }}
              >
                Quick Facts
              </Typography>

              {/* Quick Facts Items */}
              {[
                {
                  icon: BusinessCenterOutlinedIcon,
                  label: "Industry",
                  value: "Manufacturing",
                },
                {
                  icon: GroupsOutlinedIcon,
                  label: "Company Size",
                  value: "10,000+ Employees",
                },
                {
                  icon: ExtensionOutlinedIcon,
                  label: "Solution",
                  value: "Enterprise DAM Platform",
                },
                {
                  icon: ScheduleOutlinedIcon,
                  label: "Timeline",
                  value: "6 Months",
                },
                {
                  icon: BuildOutlinedIcon,
                  label: "Services",
                  value: "Strategy, Implementation, Integration, Training",
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    minHeight: "62px",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  {/* Circle Icon Container */}
                  <Box
                    sx={{
                      width: "46px",
                      height: "46px",
                      minWidth: "46px",
                      borderRadius: "50%",
                      backgroundColor: "#FAFAFA",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <GradientIcon
                      icon={item.icon}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      size={32}
                    />
                  </Box>

                  {/* Text */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 700,
                        color: "#2A2177",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: "#666666",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Our Approach Section */}
        <Box
          sx={{
            px: "84px",
            "@media (max-width: 1400px)": {
              px: "24px",
            },
            "@media (max-width: 819px)": {
              px: "16px",
            },
            pt: "48px",
            pb: "24px",
            boxSizing: "border-box",
          }}
        >
          {/* Section Header */}
          <Typography
            sx={{
              fontSize: "3rem",
              "@media (max-width: 1400px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 819px)": {
                fontSize: "2rem",
              },
              fontWeight: 700,
              color: "#2A2177",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            Our Approach
          </Typography>

          {/* Row 1 Description */}
          <Typography
            sx={{
              fontSize: "18px",
              color: "#00000099",
              lineHeight: 1.7,
              maxWidth: "840px",
              mb: 3,
            }}
          >
            We implemented a future-ready Digital Asset Management (DAM)
            solution tailored to the client's operational and collaboration
            needs.
          </Typography>

          {/* Row 1 - Three Cards */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              "@media (max-width: 1400px)": {
                flexDirection: "column",
              },
              gap: 3,
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 3,
                "@media (min-width: 1400px)": {
                  flex: 2,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                },
                "@media (max-width: 819px)": {
                  flexDirection: "column",
                },
              }}
            >
              {/* Card 1 - Strategic Foundation */}
              <ApproachCard
                title="Strategic Foundation"
                description="We started with a deep assessment of the client's existing asset ecosystem, workflows, and governance challenges. Through collaborative workshops and stakeholder alignment sessions, we defined a scalable DAM strategy, metadata structure, and long-term roadmap to support future growth."
              />

              {/* Card 2 - Solution in Action */}
              <ApproachCard
                title="Solution in Action"
                description="We deployed a centralized DAM platform integrated with the client's existing tools and processes. The implementation focused on improving asset organization, simplifying approvals, and enabling seamless collaboration across global teams through training, governance, and optimized workflows."
              />
            </Box>
            {/* Card 3 - Screenshot */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  width: "408px",
                  height: "306px",
                  borderRadius: "16px",
                  "@media (max-width: 1400px)": {
                    width: "720px",
                    height: "540px",
                  },
                  "@media (max-width: 819px)": {
                    width: "100%",
                    height: "auto",
                  },
                  boxShadow: "0px 4px 4px 0px #0000001A",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={caseStudyApp}
                  sx={{
                    width: "408px",
                    height: "306px",
                    "@media (max-width: 1400px)": {
                      width: "720px",
                      height: "540px",
                    },
                    "@media (max-width: 819px)": {
                      width: "100%",
                      height: "auto",
                    },
                    objectFit: "cover",
                    borderRadius: "16px",
                  }}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "block",
              pt: 3,
              pb: 5,
              position: "relative",
            }}
          >
            <Divider />
            <FilterVintageOutlinedIcon
              sx={{
                width: 45,
                height: 45,
                position: "absolute",
                top: "37%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                color: "#C7C7C7",
              }}
            />
          </Box>
          {/* Row 2 Description */}
          <Typography
            sx={{
              fontSize: "18px",
              color: "#00000099",
              lineHeight: 1.7,
              maxWidth: "840px",
              mb: 3,
            }}
          >
            We implemented a future-ready Digital Asset Management (DAM)
            solution tailored to the client's operational and collaboration
            needs.
          </Typography>

          {/* Row 2 - Three Cards */}
          <Box
            sx={{
              display: "flex",
              justifyContent:"space-between",
              "@media (max-width: 1400px)": {
                flexDirection: "column",
              },
              gap: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 3,
                "@media (min-width: 1400px)": {
                  flex: 2,
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                },
                "@media (max-width: 819px)": {
                  flexDirection: "column",
                },
              }}
            >
              {/* Card 1 - Process Optimization */}
              <ApproachCard
                title="Process Optimization"
                description="Optimized and standardized metadata structures, approval workflows, and asset management processes to improve efficiency and consistency across teams. This helped reduce manual effort, streamline collaboration, and speed up content delivery."
              />

              {/* Card 2 - Change Enablement */}
              <ApproachCard
                title="Change Enablement"
                description="Provided user training, adoption support, and best-practice guidance to help teams transition smoothly to the new platform. This improved collaboration, increased user confidence, and encouraged long-term adoption."
              />
            </Box>
            {/* Card 3 - Screenshot */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  width: "408px",
                  height: "306px",
                  borderRadius: "16px",
                  "@media (max-width: 1400px)": {
                    width: "720px",
                    height: "540px",
                  },
                  "@media (max-width: 819px)": {
                    width: "100%",
                    height: "auto",
                  },
                  boxShadow: "0px 4px 4px 0px #0000001A",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={caseStudyApp1}
                  sx={{
                    width: "408px",
                    height: "306px",
                    "@media (max-width: 1400px)": {
                      width: "720px",
                      height: "540px",
                    },
                    "@media (max-width: 819px)": {
                      width: "100%",
                      height: "auto",
                    },
                    objectFit: "cover",
                    borderRadius: "16px",
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* The Results Section */}
        <Box
          sx={{
            px: "84px",
            "@media (max-width: 1400px)": {
              px: "24px",
            },
            "@media (max-width: 819px)": {
              px: "16px",
            },
            py: "48px",
            boxSizing: "border-box",
          }}
        >
          {/* Section Header */}
          <Typography
            sx={{
              fontSize: "3rem",
              "@media (max-width: 1400px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 819px)": {
                fontSize: "2rem",
              },
              fontWeight: 700,
              color: "#2A2177",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            The Results
          </Typography>

          {/* Description Text */}
          <Typography
            sx={{
              fontSize: "18px",
              color: "#00000099",
              lineHeight: 1.7,
              mb: 3,
              maxWidth: "840px",
            }}
          >
            Within months of implementation, the organization experienced
            noticeable operational improvements and a more connected digital
            asset ecosystem.
          </Typography>

          {/* Content Row */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              alignItems: "flex-start",
              "@media (max-width: 1400px)": {
                flexDirection: "column",
              },
            }}
          >
            {/* Left - 2x2 Grid of Cards */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                "@media (max-width: 819px)": {
                  gridTemplateColumns: "1fr",
                },
                rowGap: 3,
                columnGap: "16px",
                flex: 1,
              }}
            >
              <ApproachCard
                icon={TaskAltOutlinedIcon}
                title="Stronger Operations & Efficiency"
                description="Teams were able to locate and manage digital assets more efficiently through improved organization and standardized workflows. This reduced operational friction, minimized duplicate content creation, and helped streamline daily collaboration across departments."
              />
              <ApproachCard
                icon={TaskAltOutlinedIcon}
                title="Better Collaboration & Consistency"
                description="With a centralized DAM environment and clear governance practices in place, teams achieved greater alignment across regions and departments. Brand assets became easier to maintain, share, and approve, leading to more consistent communication and smoother campaign execution."
              />
              <ApproachCard
                icon={TaskAltOutlinedIcon}
                title="Improvement in Team Productivity"
                description="Streamlined workflows and centralized asset management helped teams work more efficiently and collaborate with greater consistency. Faster access to approved content reduced delays, minimized duplicate work, and improved overall productivity across departments."
              />
              <ApproachCard
                icon={TaskAltOutlinedIcon}
                title="Compliance with Brand and Governance"
                description="Established governance standards and approval processes ensured all digital assets remained aligned with brand guidelines and compliance requirements. This improved content consistency, reduced risk, and maintained better control over asset usage across teams."
              />
            </Box>

            {/* Right Column */}
            <Box
              sx={{
                width: "408px",
                minWidth: "408px",
                display: "flex",
                flexDirection: "column",
                "@media (max-width: 1400px)": {
                  width: "100%",
                },
                "@media (max-width: 819px)": {
                  width: "100%",
                  minWidth: "unset",
                },
                gap: 3,
              }}
            >
              {/* Top Card - CTA */}
              <Box
                sx={{
                  width: "408px",
                  height: "166px",
                  borderRadius: "16px",
                  background: "linear-gradient(135deg, #D9EFFF, #FFFFFF)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                  boxSizing: "border-box",
                  "@media (max-width: 1400px)": {
                    width: "100%",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "#2A2177",
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Ready to Transform
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: "#2A2177",
                    mb: 2,
                  }}
                >
                  Your Digital Assets?
                </Typography>
                <Button
                  variant="contained"
                  href="/contact"
                  sx={{
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 600,
                    height: "42px",
                    width: "126px",
                    color: "#ffffff",
                    backgroundColor: "#C22A43",
                    borderRadius: "20px",
                    px: 3,
                    py: 1,
                    "&:hover": {
                      backgroundColor: "#B71C1C",
                    },
                  }}
                >
                  Contact Us
                </Button>
              </Box>

              {/* Bottom Card - More Case Studies */}
              <Box
                sx={{
                  width: "408px",
                  height: "445px",
                  borderRadius: "16px",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #0000001A",
                  boxShadow: "0px 4px 4px 0px #0000001A",
                  p: 2,
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  "@media (max-width: 1400px)": {
                    width: "100%",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: "#2A2177",
                    mb: 3,
                  }}
                >
                  More Case Studies
                </Typography>

                {[
                  {
                    img: caseStudyImg1,
                    text: "Improving Brand Consistency for a Global Retailer",
                  },
                  {
                    img: caseStudyImg2,
                    text:
                      "Streamlining Content Operations for a Healthcare Leader",
                  },
                  {
                    img: caseStudyImg3,
                    text: "Enabling Scalable Growth for a SaaS Company",
                  },
                  {
                    img: caseStudyImg4,
                    text:
                      "Streamlining Content Operations for a Healthcare Leader",
                  },
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      mb: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={item.img}
                      sx={{
                        width: "64px",
                        height: "64px",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        color: "#2A2177",
                        lineHeight: 1.4,
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                ))}
                <Divider />
                <Typography
                  sx={{
                    fontSize: "0.875rem",
                    color: "#2A2177",
                    fontWeight: 600,
                    mt: "auto",
                    cursor: "pointer",
                  }}
                >
                  View All Case Studies
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Have Questions Section */}
        <HaveQuestions />
      </Box>
    </Box>
  );
};

export default CaseStudy;
